import { Component, HostListener, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, isPlatformBrowser, NgClass, NgFor, NgIf } from '@angular/common';
import { Product as ProductModel } from '../../models/product';
import { faSearch, faFilter, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Filter } from '../../components/filter/filter';
import { ProductPage, ProductService } from '../../service/product-service';
import { Product } from '../../components/product/product';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, startWith } from 'rxjs';
import { Gender } from '../../models/gender';
import { ProductCategory } from '../../models/product-category';
import { Filter as FilterModel } from '../../models/filter';
import { DesignTheme } from '../../models/design-theme';
import { Size } from '../../models/size';
import { Color } from '../../models/color';
import { Loading } from '../../components/loading/loading';
import { ItemsLoading } from '../../components/items-loading/items-loading';
import { ItemsNotFound } from '../../components/items-not-found/items-not-found';
import { Analytics, logEvent } from '@angular/fire/analytics';
@Component({
  selector: 'app-products',
  imports: [FormsModule, Product, FontAwesomeModule, NgClass, NgIf, Filter, AsyncPipe, ItemsLoading, ItemsNotFound],
  templateUrl: './products.html',
  styleUrl: './products.scss',
  animations: [
    trigger('slideInOut', [
      state('in', style({
        transform: 'translateX(0)',
        opacity: 1,
        visibility: 'visible',
        width: '*', // full width in 'in' state
      })),
      state('out', style({
        transform: 'translateX(-100%)',
        opacity: 0,
        visibility: 'hidden',
        overflow: 'hidden'
      })),
      transition('in => out', [
        animate('300ms ease-in-out')
      ]),
      transition('out => in', [
        // No animation needed on show, just appear instantly
        animate('0ms')
      ])
    ])
  ]

})
export class Products implements OnInit {
  private route = inject(ActivatedRoute)
  private analytics = inject(Analytics);
  private platformId = inject(PLATFORM_ID);
  faSearch = faSearch;
  faFilter = faFilter;
  faBoxOpen = faBoxOpen


  showFilters = true;
  lgScreen = false;
  searchQuery = '';
  isFilterVisible = true;

  private productService = inject(ProductService)
  protected products$: Observable<ProductModel[]> | undefined
  // protected paginationInfo$: Observable<any> | undefined
  private paginationSubject = new BehaviorSubject<PaginationInfo>({
    currentPage: 1,
    pageCount: 0,
    currentPageCount: 0,
    totalCount: 0,
    from: 0,
    to: 0
  });

  paginationInfo$ = this.paginationSubject.asObservable();

  filters: FilterModel = {
    gender: null,
    categories: [],
    color: null,
    themes: [],
    sizes: [],
    price: 0
  };

  sort: 'price' | 'newest' | null = null;

  colors: Color[] | null = [
    { name: 'Black', code: '#000000' },
    { name: 'White', code: '#FFFFFF' },
    { name: 'Navy Blue', code: '#0B3C5D' },
    { name: 'Grey', code: '#808080' },
    { name: 'Light Grey', code: '#D3D3D3' },
    { name: 'Red', code: '#C0392B' },
    { name: 'Blue', code: '#1F618D' },
    { name: 'Green', code: '#1E8449' },
    { name: 'Olive Green', code: '#556B2F' },
    { name: 'Yellow', code: '#F1C40F' },
    { name: 'Maroon', code: '#800000' },
    { name: 'Brown', code: '#6E2C00' },
    { name: 'Beige', code: '#F5F5DC' },
    { name: 'Cream', code: '#FFFDD0' },
    { name: 'Pink', code: '#F1948A' },
    { name: 'Purple', code: '#6C3483' }
  ];
  categories: string[] | null = Object.values(ProductCategory);
  themes: string[] | null = Object.values(DesignTheme);
  gender: string[] | null = Object.values(Gender);
  sizes: string[] | null = Object.values(Size);

  ngOnInit() {
    if(isPlatformBrowser(this.platformId)){
      logEvent(this.analytics, 'products_page_view', { page_path: '/products' });
    }
    this.onResize();
    // this.loadPage();
    // this.route.queryParams.subscribe(params => {
    //   const category = params['category'];
    //   console.log(category);
    //   this.filters.categories.push(category)

    //   // Filter products here
    // });
  }

  loadPage() {
    this.products$ = this.productService.getProductObservable(this.filters, this.sort, this.searchQuery);
    this.paginationInfo$ = this.productService.getPaginationInfoObservable()
  }

  onPageChange(event: Event, page: number): void {
    event.preventDefault();
    this.productService.goToPage(page);
    this.products$ = this.productService.getProductObservable(this.filters, this.sort, this.searchQuery);
  }

  onPrevious(event: Event): void {
    event.preventDefault();
    this.products$ = this.productService.previousPage(this.filters, this.sort, this.searchQuery);
  }

  onNext(event: Event): void {
    event.preventDefault();
    this.products$ = this.productService.nextPage(this.filters, this.sort, this.searchQuery);
  }

  getPageNumbers(currentPage: number, pageCount: number): number[] {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pageCount, startPage + maxVisiblePages - 1);

    // Adjust if we're at the start or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  @HostListener('window:resize')
  onResize() {
    this.lgScreen = window.innerWidth >= 1024;
    this.showFilters = this.lgScreen; // Always show on desktop
  }

  applyFilters() {
    // this.products$ = this.productService.getProductObservable(this.filters, this.sort, this.searchQuery);
    this.loadPage();
  }

  onSearch() {
    this.products$ = this.productService.getProductObservable(this.filters, this.sort, this.searchQuery);
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }
}
